import React from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { i18n } from 'i18n';
import { PCStope } from '../MineModel/Stopes';

type AddAllOption = { addAllStopes: true };
type StopeOption = PCStope | AddAllOption;

const ADD_ALL_SENTINEL: AddAllOption = { addAllStopes: true };
const stopeFilter = createFilterOptions<StopeOption>();

const isAddAllSentinel = (option: StopeOption): option is AddAllOption => (option as AddAllOption).addAllStopes === true;

const stopeLabel = (stope: PCStope): string => stope.data?.stopeName ?? stope.data?.stopeId ?? '';

interface StopeMultiSelectProps {
  stopes: PCStope[];
  value: PCStope[];
  onChange: (stopes: PCStope[]) => void;
  disabled?: boolean;
  error?: boolean;
}

// Multi-select with an "Add all" option that adds every stope matching the typed filter.
const StopeMultiSelect = ({
  stopes,
  value,
  onChange,
  disabled,
  error,
}: StopeMultiSelectProps) => {
  const [inputValue, setInputValue] = React.useState('');
  const filteredStopesRef = React.useRef<PCStope[]>([]);
  const addAllLabel = inputValue ? i18n._('Add Matching Stopes') : i18n._('Add All Stopes');

  const filterOptions = (options: StopeOption[], state: Parameters<typeof stopeFilter>[1]): StopeOption[] => {
    const filtered = stopeFilter(options, state) as PCStope[];
    filteredStopesRef.current = filtered;
    const selectedIds = new Set(value.map((stope) => stope.data.stopeId));
    return filtered.some((stope) => !selectedIds.has(stope.data.stopeId)) ? [ADD_ALL_SENTINEL, ...filtered] : filtered;
  };

  const handleChange = (_event: React.SyntheticEvent, next: StopeOption[]) => {
    if (!next.some(isAddAllSentinel)) {
      onChange(next as PCStope[]);
      return;
    }
    const selectedIds = new Set(value.map((stope) => stope.data.stopeId));
    onChange([...value, ...filteredStopesRef.current.filter((stope) => !selectedIds.has(stope.data.stopeId))]);
  };

  return (
    <Autocomplete<StopeOption, true, false, false>
      multiple
      size="small"
      options={stopes}
      value={value}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      filterOptions={filterOptions}
      getOptionLabel={(option) => (isAddAllSentinel(option) ? addAllLabel : stopeLabel(option))}
      isOptionEqualToValue={(option, selected) => !isAddAllSentinel(option)
        && !isAddAllSentinel(selected)
        && option.data?.stopeId === selected.data?.stopeId}
      onChange={handleChange}
      disableCloseOnSelect
      limitTags={8}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return isAddAllSentinel(option)
          ? (
            <li key="add-all-stopes" {...optionProps}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{addAllLabel}</Typography>
            </li>
          )
          : <li key={key} {...optionProps}>{stopeLabel(option)}</li>;
      }}
      renderInput={(params) => (
        <TextField {...params} color="secondary" label={i18n._('Select Stopes')} error={error} />
      )}
    />
  );
};

export default StopeMultiSelect;
